import { styled, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";

import "./Drawer.css";
import { LinksMenu } from "./Links";
import { createExamBuilder, createExamOverview } from "./LinksData";
import * as C from "../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DIALOGS, DIALOG_TYPES } from "../Dialogs/dialogReducer";
import { RootState } from "../..";
import { MAIN_TYPES } from "../ExamBuilder/Reducers/MainReducer";
import { MANIPULATE_EXERCISES_TYPES } from "../ExamBuilder/Reducers/ManipulateExercisesReducer";
import { checkChangesLeavePage, saveExam } from "../ExamBuilder/Functions/FunctionsMain";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

export default function MiniDrawer() {
  const open = false;
  const examOverview = createExamOverview(openDialogNewExam, openDialogNewFolder, open);
  const examBuilder = createExamBuilder(
    openDialogTopicsExams,
    openDialogExcercises,
    openDialogPrint,
    openDialogChangeLayout,
    toggleShowSolutions,
    handleSaveExam,
    handleCheckChangesLeavePage,
    open
  );
  const { pathname } = useLocation();
  const basePath = "/" + pathname.split("/")[1];
  const isDemoMode: boolean = pathname.startsWith("/demo");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathString = decodeURI(location.pathname.replace("/exam-builder", "")) || "/";

  const showSolutions = useSelector((state: RootState) => state.examBuilder.main.showSolutions);
  const examData = useSelector((state: RootState) => state.examBuilder.main.examData);
  const selectedObjectiveId = useSelector((state: RootState) => state.examBuilder.main.selectedObjectiveId);
  const nameChanged = useSelector((state: RootState) => state.examBuilder.main.nameChanged);
  const hasUnsavedChanges = useSelector((state: RootState) => state.examBuilder.main.hasUnsavedChanges);

  const user = useSelector((state: RootState) => state.app.user);
  const userId = user?.uid;

  function openDialogNewExam() {
    dispatch({ type: DIALOG_TYPES.OPEN_DIALOG, payload: { dialogName: DIALOGS.ADD_EXAM_DIALOG } });
  }

  function openDialogNewFolder() {
    dispatch({ type: DIALOG_TYPES.OPEN_DIALOG, payload: { dialogName: DIALOGS.ADD_FOLDER_DIALOG } });
  }

  function openDialogTopicsExams() {
    dispatch({ type: DIALOG_TYPES.OPEN_DIALOG, payload: { dialogName: DIALOGS.SELECT_TOPICS_EXAM_DIALOG } });
  }

  function openDialogExcercises() {
    if (selectedObjectiveId) {
      const indexOfNewExercises = examData.objectives[selectedObjectiveId].exercises.length;
      dispatch({ type: MANIPULATE_EXERCISES_TYPES.INDEX_NEW_EXERCISES, payload: indexOfNewExercises });
      dispatch({ type: DIALOG_TYPES.OPEN_DIALOG, payload: { dialogName: DIALOGS.SELECT_EXERCISES_EXAM_DIALOG } });
    }
  }

  function openDialogPrint() {
    dispatch({ type: DIALOG_TYPES.OPEN_DIALOG, payload: { dialogName: DIALOGS.PRINT_EXAM_DIALOG } });
  }

  function openDialogChangeLayout() {
    dispatch({ type: DIALOG_TYPES.OPEN_DIALOG, payload: { dialogName: DIALOGS.CHANGE_LAYOUT_DIALOG } });
  }

  function toggleShowSolutions() {
    const newStateShowSolutions = !showSolutions;
    dispatch({ type: MAIN_TYPES.SHOW_SOLUTIONS, payload: newStateShowSolutions });
  }

  async function handleSaveExam() {
    if (userId) {
      saveExam(userId, nameChanged, examData, pathString, isDemoMode);
    }
  }

  function handleCheckChangesLeavePage() {
    checkChangesLeavePage(hasUnsavedChanges, navigate, pathString, isDemoMode);
  }

  const drawerData = (() => {
    switch (basePath) {
      case C.PRIVATE_ROUTES.EXAM_OVERVIEW:
        return examOverview;
      case C.PRIVATE_ROUTES.EXAM_BUILDER:
        return examBuilder;
      case C.PUBLIC_ROUTES.EXAM_BUILDER_DEMO:
        return examBuilder;
      default:
        return [];
    }
  })();

  return (
    <Box sx={{ display: "flex" }}>
      <Drawer variant="permanent" open={open} className="drawer-content">
        <DrawerHeader />
        <List>
          <LinksMenu settings={drawerData} showSolutions={showSolutions} />
        </List>
        <Box sx={{ flex: 1, backgroundColor: "white" }} />
      </Drawer>
    </Box>
  );
}
